import type ViewType from "../../../constants/ViewType";
import type { SectionError } from "../../../utils/useSectionErrors";
import type { InlineQuestionProps } from "../InlineQuestion";
import type { ViewStyle } from "react-native";

import QualificationLimitNotification from "./QualificationLimitNotification/QualificationLimitNotification";
import SubmissionFailureNotification from "./SubmissionFailureNotification/SubmissionFailureNotification";
import { Flex, Box } from "../../../../../../../../mads";
import { PrimaryHeading, type PrimaryHeadingProps } from "../PrimaryHeading";
import {
  TileSection,
  type TileSectionProps,
  type TileSectionSentimentCounts,
} from "../TileSection";
import { VJNegativeSpacing } from "../VJNegativeSpacing";

import React from "react";

export type AttributeCollectionProps = {
  subtitleProps?: PrimaryHeadingProps["subtitleProps"];
  title: string;
  sections: TileSectionProps[];
  hasDivider?: boolean;
  errors?: SectionError[] | number[];
  onClearError: () => void;
  viewType?: ViewType;
  sentimentCounts?: TileSectionSentimentCounts;
  missingQualifications?: Array<InlineQuestionProps>;
  sx?: ViewStyle;
};

const Errors: React.FC<Pick<AttributeCollectionProps, "errors" | "onClearError" | "title">> = ({
  errors = [],
  onClearError,
  title,
}) => {
  let toastSequence = 0;
  return (
    <>
      {errors.map((error) => {
        const key = `${title}${toastSequence++}`;

        // Support both legacy number[] format and new SectionError[] format
        let showQualificationLimit = false;

        if (typeof error === "object" && typeof error?.error?.message === "string") {
          // Check if message contains SDAPI error codes 432 or 433
          // This works consistently across iOS and Android
          const errorMessage = error?.error?.message ?? "";
          showQualificationLimit = errorMessage.includes("432") || errorMessage.includes("433");
        }

        if (showQualificationLimit) {
          return (
            <QualificationLimitNotification key={key} id={key} handleClearError={onClearError} />
          );
        }

        return <SubmissionFailureNotification key={key} id={key} handleClearError={onClearError} />;
      })}
    </>
  );
};

const TitleSections: React.FC<Pick<AttributeCollectionProps, "sections">> = ({ sections }) => {
  return (
    <>
      {sections.map((sectionProps: TileSectionProps) => {
        return (
          <TileSection
            key={`${sectionProps.type}Section-${sectionProps.title}`}
            {...sectionProps}
          />
        );
      })}
    </>
  );
};
export const AttributeCollection: React.FC<
  AttributeCollectionProps & {
    children?: React.ReactNode;
  }
> = ({
  sections,
  subtitleProps,
  title,
  hasDivider = true,
  errors = [],
  onClearError,
  children,
  sx,
}) => {
  return (
    <VJNegativeSpacing hasDivider={hasDivider} sx={sx}>
      <PrimaryHeading title={title} subtitleProps={subtitleProps} />
      <Flex sx={{ gap: 24, flexDirection: "column" }}>
        <TitleSections sections={sections} />
      </Flex>

      {children && <Box sx={{ marginTop: 16 }}>{children}</Box>}

      <Errors errors={errors} onClearError={onClearError} title={title} />
    </VJNegativeSpacing>
  );
};
